import { InputGroup, Input, InputRightElement, forwardRef, InputProps, Icon, Flex, Text } from '@chakra-ui/react';
import { useResource } from 'datx-jsonapi-react';

import IconRight from '@/assets/icons/ic-arrow-next.svg';
import IconLeft from '@/assets/icons/ic-arrow-prev.svg';
import SearchIcon from '@/assets/icons/ic-search.svg';

import { SlugIdParam, StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryLectureAccessPermission } from '@/queries/lecture-access-permissions';
import { FoundIn, OmnisearchOccurrence } from '@/resources/OmnisearchOccurrence';

interface ISearchInputProps extends InputProps {
  data: Array<OmnisearchOccurrence>;
  isAutocompleteOpen: boolean;
}

export const OmnisearchSearchInput = forwardRef<ISearchInputProps, 'input'>((props, ref) => {
  const { isAutocompleteOpen, placeholder, value, defaultValue, onChange, data, ...rest } = props;
  const { index, t, id } = useQueryParams<{ index: string; t: string; query: string; id: string }>({
    id: SlugIdParam,
    query: StringParam,
    searchScope: StringParam,
    index: StringParam,
  });

  const { data: lectureData } = useResource(queryLectureAccessPermission(id));

  const posterOccurrences = data?.find((d) => d.foundIn === FoundIn.POSTER_DOCUMENT);
  const videoOccurrences = data?.find((d) => d.foundIn === FoundIn.TRANSCRIPT_EN);

  const videoMatchOpen = t && videoOccurrences;
  const posterMatchOpen = index && posterOccurrences;

  return (
    <InputGroup {...rest}>
      <Input
        ref={ref}
        color="black"
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        borderRadius="full"
        placeholder={placeholder}
        aria-label={placeholder}
        isDisabled={lectureData?.restricted}
      />
      <InputRightElement pl={0} pr={2} minW="max-content" display="flex">
        {videoMatchOpen && <VideoMatchOpen data={data} />}
        {posterMatchOpen && <PosterMatchOpen data={data} />}
        {!videoMatchOpen && !posterMatchOpen && <Icon as={SearchIcon} color="black" boxSize="24px" />}
      </InputRightElement>
    </InputGroup>
  );
});

const VideoMatchOpen = ({ data }) => {
  const { t, change } = useQueryParams<{
    t: string;
    posterExpanded: string;
  }>({
    t: StringParam,
    posterExpanded: StringParam,
  });

  const videoOccurrences = data?.find((d) => d.foundIn === FoundIn.TRANSCRIPT_EN);

  const videoOccurrenceIndex = videoOccurrences.details.findIndex((vo) => {
    const highlightedOccurrence = vo.find((c) => c.highlight);
    const timestamp = Math.round(highlightedOccurrence.position.timestamp || 0);

    return parseInt(t) === timestamp;
  });

  function changeTimestampAtIndex(index: number) {
    const highlightedOccurrence = videoOccurrences.details[index].find((c) => c.highlight);
    const timestamp = Math.round(highlightedOccurrence.position.timestamp || 0);

    change({ t: timestamp.toString(), posterExpanded: 'false' }, { method: 'replace', shallow: true });
  }

  const isLast = videoOccurrenceIndex + 1 === videoOccurrences.details.length;

  return (
    <Flex alignItems="center" color="black" mr="2">
      {videoOccurrenceIndex !== 0 && (
        <Icon
          _hover={{ cursor: 'pointer' }}
          tabIndex={0}
          role="button"
          onClick={() => changeTimestampAtIndex(videoOccurrenceIndex - 1)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              changeTimestampAtIndex(videoOccurrenceIndex - 1);
            }
          }}
          w="14px"
          h="14px"
          as={IconLeft}
          aria-label="Previous video match"
        />
      )}
      <Text mx="2" mt="2px" fontSize="xs" fontWeight="semibold">
        {videoOccurrenceIndex + 1}/{videoOccurrences.details.length}
      </Text>
      {!isLast && (
        <Icon
          _hover={{ cursor: 'pointer' }}
          tabIndex={0}
          role="button"
          onClick={() => changeTimestampAtIndex(videoOccurrenceIndex + 1)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              changeTimestampAtIndex(videoOccurrenceIndex + 1);
            }
          }}
          w="14px"
          h="14px"
          as={IconRight}
          aria-label="Next video match"
        />
      )}
    </Flex>
  );
};

const PosterMatchOpen = ({ data }) => {
  const { index, change } = useQueryParams<{
    index: string;
    posterExpanded: string;
  }>({
    index: StringParam,
    posterExpanded: StringParam,
  });

  const posterOccurrences = data?.find((d) => d.foundIn === FoundIn.POSTER_DOCUMENT);

  function changeTimestampAtIndex(occurrenceIndex: number) {
    change({ index: occurrenceIndex.toString(), posterExpanded: 'true' }, { method: 'replace', shallow: true });
  }

  const posterOccurrenceIndex = parseInt(index);

  const isLast = posterOccurrenceIndex + 1 === posterOccurrences.details.length;

  return (
    <Flex alignItems="center" color="black" mr="2">
      {posterOccurrenceIndex !== 0 && (
        <Icon
          _hover={{ cursor: 'pointer' }}
          tabIndex={0}
          role="button"
          onClick={() => changeTimestampAtIndex(posterOccurrenceIndex - 1)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              changeTimestampAtIndex(posterOccurrenceIndex - 1);
            }
          }}
          w="14px"
          h="14px"
          as={IconLeft}
          aria-label="Previous poster match"
        />
      )}
      <Text mx="2" mt="2px" fontSize="xs" fontWeight="semibold">
        {posterOccurrenceIndex + 1}/{posterOccurrences.details.length}
      </Text>
      {!isLast && (
        <Icon
          _hover={{ cursor: 'pointer' }}
          tabIndex={0}
          role="button"
          onClick={() => changeTimestampAtIndex(posterOccurrenceIndex + 1)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              changeTimestampAtIndex(posterOccurrenceIndex + 1);
            }
          }}
          w="14px"
          h="14px"
          as={IconRight}
          aria-label="Next poster match"
        />
      )}
    </Flex>
  );
};
